/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { ComponentChildren } from 'preact';
import { Ref } from 'preact/compat';
import { HTMLAttributesSignalExcluded } from '../utils/UNSAFE_attributeUtils';
import { ColorProps } from '../utils/UNSAFE_interpolations/colors';

type IntrinsicProps = Pick<HTMLAttributesSignalExcluded<HTMLDivElement>, 'id' | 'class'>;
type WrapperProps = IntrinsicProps & {
  color?: ColorProps['color'];
  borderWidth?: string;
  ariaProps?: {
    'aria-valuenow'?: HTMLAttributesSignalExcluded['aria-valuenow'];
    'aria-valuemin'?: HTMLAttributesSignalExcluded['aria-valuemin'];
    'aria-valuemax'?: HTMLAttributesSignalExcluded['aria-valuemax'];
    'aria-valuetext'?: HTMLAttributesSignalExcluded['aria-valuetext'];
    'aria-label'?: HTMLAttributesSignalExcluded['aria-label'];
    role?: HTMLAttributesSignalExcluded['role'];
  };
  clipPath?: string;
  wrapperRef?: Ref<HTMLDivElement>;
  width?: string;
  height?: string;
  children?: ComponentChildren;
};

export const CircleWrapper = ({
  id,
  children,
  color,
  clipPath,
  borderWidth,
  ariaProps,
  wrapperRef,
  width,
  height,
  ...otherProps
}: WrapperProps) => {
  return (
    <div
      ref={wrapperRef}
      id={id}
      {...ariaProps}
      class={otherProps.class}
      style={{
        borderColor: color,
        borderWidth: borderWidth,
        clipPath: clipPath,
        width,
        height
      }}>
      {children}
    </div>
  );
};

type CircleInnerProps = Pick<IntrinsicProps, 'class'> & {
  clipPath?: string;
  color?: ColorProps['color'];
  width?: string;
  height?: string;
  size?: string;
  section?: string;
};

function getBorderWidth(section: string, borderWidth: string | undefined) {
  const border: Record<string, string | undefined> = {
    borderTopWidth: borderWidth,
    borderBottomWidth: borderWidth,
    borderRightWidth: borderWidth,
    borderLeftWidth: borderWidth
  };
  switch (section) {
    case 'top':
      border['borderBottomWidth'] = undefined;
      break;
    case 'bottom':
      border['borderTopWidth'] = undefined;
      break;
    case 'left':
      border['borderRightWidth'] = undefined;
      break;
    case 'right':
      border['borderLeftWidth'] = undefined;
      break;
  }
  return border;
}

export const CircleInner = ({
  clipPath,
  color,
  height,
  width,
  size,
  section = 'full',
  ...otherProps
}: CircleInnerProps) => {
  const borderWidth = getBorderWidth(section, size);
  return (
    <div
      class={otherProps.class}
      style={{
        clipPath: clipPath,
        borderColor: color,
        height,
        width,
        ...borderWidth
      }}></div>
  );
};
